export const WIDGET_TYPE = 'live-multi-values'

// Pipes events

export interface LiveMultiValuesState {
    metaByLayer: Map<number, LiveMultiValuesMeta>
    meta: LiveMultiValuesMeta // @meta params merged from all layers, giving priority to values on the first layer (and merging the channels list)
    values: LiveValue[]
}

export interface LiveValue extends LiveMultiValuesEvent {
    layer: number
    // effective (custom setting or channel metadata) thresholds, resolved by the widget before rendering
    minThresholds?: number
    maxThresholds?: number
    minThresholdColor?: string
    maxThresholdColor?: string
}

export interface LiveMultiValuesEvent extends LiveEvent {
    // optional: a "simple query" (no channels @meta parameter) can emit title-only events with no
    // mnemonic at all - mergeValues (reducer.ts) falls back to title for both the dedup key and the
    // stored value's own mnemonic in that case, but the field itself is never guaranteed present.
    mnemonic?: string
    title?: string // filled automatically from the data on the channels @meta parameter, when it is provided, or from the mnemonic value otherwise
    value?: string | number // absent for a channel's placeholder row (selected but no value received yet)
    uom?: string
    __uom?: string // compatibility with queries made for the Grid widget: uom has priority
    color?: string
    __color?: string // compatibility with queries made for the Grid widget: color has priority
}

export interface LiveMultiValuesMeta extends Record<string, unknown> {
    channels?: ChannelMetadata[] // optional, displays all received values if not provided
    hideMnemonicsInSelector?: boolean // optional, hides the mnemonic names from the channels selector
    boxMinWidth?: string // ex: 100px (default value depend on the box size)
    boxDefaultColor?: string
    widgetTitle?: string
}

export type LiveMultiValuesMessage = FlowMessage<LiveMultiValuesEvent, LiveMultiValuesMeta>

export interface ChannelMetadata {
    mnemonic: string
    title?: string // same as mnemonic if not provided
    uom?: string
    color?: string
    category?: string // text to be displayed in the channels selector (optional)
    transient?: boolean // if true the box will be removed from the grid when no value is received in the last batch (optional)
    minThresholds?: number // optional, no lower bound check when not provided
    maxThresholds?: number // optional, no upper bound check when not provided
    minThresholdColor?: string // optional, defaults to a shared danger red when not provided
    maxThresholdColor?: string // optional, defaults to a shared danger red when not provided
    selected?: boolean // optional default pre-selection, applied only the first time this mnemonic is ever seen (see resolveNewChannelDefaults)
}

// Widget config

export type ViewMode = 'cards' | 'table'

// the two alternative ways to show a channel's color - mutually exclusive, chosen in the Display
// tab. colorTextEnabled (below) is a separate, complementary toggle: it can be combined with either
// one, or turned off entirely to leave the channel name in its plain, standard color.
export type ColorStyle = 'circle' | 'bar'

// This is the widget's one canonical, DB-shared setup - the same object every viewer of the
// dashboard and the widget's own editor all read (Live's jsonConfig has no per-viewer split for
// custom plugin fields; the only such split in the platform is a Y-axis-specific one this plugin
// can't reuse - see viewerOverride.ts for the browser-local layer built on top of this).
export type LiveMultiValuesWidgetConfig = LiveWidgetConfig & {
    boxSize: string
    selectedChannels: CustomSettingsByChannel[]
    // mnemonics explicitly resolved to "not shown" - by a user Remove, or by a false/absent `selected`
    // @meta default the first time the mnemonic was ever seen. Keeps `resolveNewChannelDefaults` from
    // re-evaluating (and silently re-adding) a channel the user already decided about.
    excludedChannels: string[]
    viewMode: ViewMode
    colorStyle: ColorStyle
    colorTextEnabled: boolean
    widgetType: string
}

export const WIDGET_DEFAULT_CONFIG: LiveMultiValuesWidgetConfig = {
    hideXAxis: false, // this option makes the 'Restore Chart Defaults' option to show in the widget 'Chart Options'
    boxSize: 'small',
    selectedChannels: [],
    excludedChannels: [],
    viewMode: 'cards',
    colorStyle: 'circle',
    colorTextEnabled: false,
    widgetType: WIDGET_TYPE // used to intercept widget requests in order to populate the live_widget_multi_values_identity lookup
}

export const WIDGET_POLICIES: LiveWidgetPolicies = {
    handleOwnMissingData: true,
    defaultReducer: '@latest'
}

// Selected channels settings customization

// each field is null (not just absent) to mean "no override, fall back to the channel/query default" -
// that's an explicit, persisted value (e.g. after clicking a Customize drawer's Reset), distinct from
// undefined/never-set. Both null and undefined are handled the same way at every read site (`?? `).
export interface CustomSettings {
    customTitle?: string | null
    customColor?: string | null
    customMinThresholds?: number | null
    customMaxThresholds?: number | null
    customMinThresholdColor?: string | null
    customMaxThresholdColor?: string | null
}

export interface CustomSettingsByChannel {
    mnemonic: string
    settings: CustomSettings
}

export interface ChannelSelectionResolution {
    selectedChannels: CustomSettingsByChannel[]
    changed: boolean
}

// Applies the `selected` @meta default for any channel that (a) isn't already selected and (b) the
// user hasn't explicitly removed before. A `false`/absent default needs no bookkeeping at all and is
// safe to re-evaluate on every call: it just produces "not selected" again, harmlessly - so a query
// that adds `selected: true` to a channel later takes effect on the very next render, and a widget
// that predates this feature never gets its untouched channels permanently locked out on first load.
// `excludedChannels` is therefore only ever the sticky memory of explicit user Removes, never written
// here - which is also what makes it safe for `changed` to stay false when there's nothing to add.
export const resolveNewChannelDefaults = (
    channels: ChannelMetadata[] | undefined,
    selectedChannels: CustomSettingsByChannel[],
    excludedChannels: string[]
): ChannelSelectionResolution => {
    const selectedMnemonics = new Set(selectedChannels.map(channel => channel.mnemonic))
    const excludedMnemonics = new Set(excludedChannels)

    const toAdd = (channels ?? []).filter(
        channel =>
            channel.selected === true && !selectedMnemonics.has(channel.mnemonic) && !excludedMnemonics.has(channel.mnemonic)
    )

    if (toAdd.length === 0) {
        return { selectedChannels, changed: false }
    }

    return {
        selectedChannels: [
            ...selectedChannels,
            ...toAdd.map(channel => ({ mnemonic: channel.mnemonic, settings: { customTitle: null, customColor: null } }))
        ],
        changed: true
    }
}

// sentinel value for CustomSettings.customColor meaning "hide the color indicator", as opposed
// to null/undefined (use the default color) or any other string (a custom color)
export const HIDE_COLOR_VALUE = 'hide_color'

// used for a threshold's arrow/highlight when neither the channel metadata nor its custom settings provide one
export const DEFAULT_THRESHOLD_COLOR = '#E02020'
